import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { hieraApi } from "../lib/api";
import { t, type Lang } from "../lib/i18n";
import type { Me } from "../lib/api";
import { HIERA_WEB_VERSION } from "../version";
import { AgentChat } from "../shell/AgentChat";

type Recent = Awaited<ReturnType<typeof hieraApi.home>>["recent"][number];
type Space = Awaited<ReturnType<typeof hieraApi.home>>["spaces"][number];
type Props = { lang: Lang; me: Me; onChanged: () => void };
type ActivityKind = "created" | "changed" | "version";
type ActivityFilter = "all" | "created" | "changed" | "mine";

function relTime(iso: string, lang: Lang): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return lang === "ru" ? `${Math.max(mins, 1)} мин` : `${Math.max(mins, 1)}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return lang === "ru" ? `${hours} ч` : `${hours}h`;
  return new Date(iso).toLocaleDateString(lang === "ru" ? "ru-RU" : "en-GB", {
    day: "numeric",
    month: "short",
  });
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function dayDiff(iso: string): number {
  const d = new Date(iso);
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((start - day) / 86400000);
}

function activityBucket(iso: string, lang: Lang): string {
  const diff = dayDiff(iso);
  if (diff === 0) return t(lang, "today");
  if (diff === 1) return t(lang, "yesterday");
  if (diff >= 2 && diff < 7) return t(lang, "thisWeek");
  return t(lang, "older");
}

function isCreated(row: Recent): boolean {
  const created = new Date(row.created_at).getTime();
  const updated = new Date(row.updated_at).getTime();
  return Number.isFinite(created) && Math.abs(updated - created) < 2 * 60 * 1000;
}

function activityKind(row: Recent): ActivityKind {
  if (isCreated(row)) return "created";
  if (row.version > 1) return "version";
  return "changed";
}

function verb(row: Recent, lang: Lang): string {
  const kind = activityKind(row);
  if (kind === "created") return t(lang, "createdVerb");
  if (kind === "version") return `${t(lang, "versionN")} ${row.version}`;
  return t(lang, "changedVerb");
}

function letters(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  const a = words[0]?.[0] || "";
  const b = words[1]?.[0] || words[0]?.[1] || "";
  return (a + b).toUpperCase() || "·";
}

const SPACE_HUES = [265, 210, 195, 155, 25, 340, 300, 230];

function spaceHue(title: string): number {
  let h = 0;
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) >>> 0;
  return SPACE_HUES[h % SPACE_HUES.length];
}

function membersLabel(n: number, lang: Lang): string {
  const count = Math.max(0, Number(n) || 0);
  if (lang !== "ru") return count === 1 ? "1 member" : `${count} members`;
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} участник`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${count} участника`;
  return `${count} участников`;
}

function DocGlyph() {
  return (
    <svg className="home-tile-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path d="M7 3.5h7l4 4V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" strokeLinejoin="round" />
      <path d="M14 3.5V8h4.5" strokeLinejoin="round" />
      <path d="M9 12h6M9 15.5h4" strokeLinecap="round" />
    </svg>
  );
}

function PageTile({ icon }: { icon?: string | null }) {
  const emoji = String(icon || "").trim();
  return (
    <span className="home-tile home-tile--page" aria-hidden>
      {emoji ? emoji : <DocGlyph />}
    </span>
  );
}

function SpaceAvatar({ icon, title }: { icon?: string | null; title: string }) {
  const emoji = String(icon || "").trim();
  const hue = spaceHue(title);
  return (
    <span
      className="ws-avatar"
      aria-hidden
      style={{
        background: `hsl(${hue} 48% 42%)`,
        boxShadow: `0 0 0 1px hsl(${hue} 40% 32% / 0.45)`,
      }}
    >
      {emoji || letters(title)}
    </span>
  );
}

function WorkspaceBlocks({
  lang,
  spaces,
  limit,
}: {
  lang: Lang;
  spaces: Space[];
  limit?: number;
}) {
  const show = typeof limit === "number" ? spaces.slice(0, limit) : spaces;
  const showAllLink = typeof limit === "number" && spaces.length > 0;
  return (
    <section className="home-section home-workspaces">
      <h2>{t(lang, "spacesYouIn")}</h2>
      {spaces.length ? (
        <>
          <ul className="ws-grid">
            {show.map((s) => (
              <li key={s.id}>
                <Link className="ws-card" to={`/s/${s.id}`} title={s.title}>
                  <SpaceAvatar icon={s.icon} title={s.title} />
                  <div className="ws-card-body">
                    <strong>{s.title}</strong>
                    <span>{membersLabel(s.member_count, lang)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {showAllLink ? (
            <div className="ws-more">
              <Link to="/?tab=spaces">{t(lang, "viewAllSpaces")} →</Link>
            </div>
          ) : null}
        </>
      ) : (
        <MascotEmpty text={t(lang, "emptyContinue")} />
      )}
    </section>
  );
}

function EditorAvatar({ name, avatar }: { name: string | null; avatar: string | null }) {
  if (!name) return null;
  const raw = String(avatar || "").trim();
  const url = /^https?:\/\//i.test(raw) ? raw : "";
  const emoji = url ? "" : raw;
  return (
    <span className="activity-avatar" title={name} aria-hidden>
      {url ? <img src={url} alt="" /> : emoji || letters(name)}
    </span>
  );
}

function ActionMark({ kind }: { kind: ActivityKind }) {
  return (
    <span className={`activity-action is-${kind}`} aria-hidden>
      {kind === "created" ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M7 3.5h7l4 4V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" strokeLinejoin="round" />
          <path d="M14 3.5V8h4.5" strokeLinejoin="round" />
          <path d="M12 12v5M9.5 14.5h5" strokeLinecap="round" />
        </svg>
      ) : kind === "version" ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M7 4h7l4 4v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" strokeLinejoin="round" />
          <path d="M14 4v4h4" strokeLinejoin="round" />
          <path d="M9 13h6M9 16h4" strokeLinecap="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3Z" strokeLinejoin="round" />
          <path d="m13.5 6.5 3 3" strokeLinecap="round" />
        </svg>
      )}
    </span>
  );
}

function MascotEmpty({ text }: { text: string }) {
  return (
    <div className="mascot-empty">
      <img src={`/mascot.png?v=${HIERA_WEB_VERSION}`} alt="" width={72} height={72} />
      <p>{text}</p>
    </div>
  );
}

function ActivitySkeleton() {
  return (
    <aside className="activity" aria-busy="true" aria-label="…">
      <div className="activity-skel activity-skel-title" />
      <div className="activity-skel activity-skel-filters" />
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="activity-skel-row">
          <div className="activity-skel activity-skel-ico" />
          <div className="activity-skel-lines">
            <div className="activity-skel activity-skel-line short" />
            <div className="activity-skel activity-skel-line" />
            <div className="activity-skel activity-skel-line mid" />
          </div>
        </div>
      ))}
    </aside>
  );
}

function Activity({ lang, me, rows }: { lang: Lang; me: Me; rows: Recent[] }) {
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const q = query.trim().toLowerCase();
  const mine = me.displayName.trim().toLowerCase();
  const filtered = rows.filter((row) => {
    const kind = activityKind(row);
    if (filter === "created" && kind !== "created") return false;
    if (filter === "changed" && kind === "created") return false;
    if (filter === "mine" && (row.editor || "").trim().toLowerCase() !== mine) return false;
    if (!q) return true;
    const hay = `${row.title || ""} ${row.editor || ""} ${row.space_title || ""}`.toLowerCase();
    return hay.includes(q);
  });

  const order = [t(lang, "today"), t(lang, "yesterday"), t(lang, "thisWeek"), t(lang, "older")];
  const map = new Map<string, Recent[]>();
  for (const label of order) map.set(label, []);
  for (const row of filtered.slice(0, 24)) {
    map.get(activityBucket(row.updated_at, lang))?.push(row);
  }
  const groups = order.map((label) => ({ label, rows: map.get(label) || [] })).filter((g) => g.rows.length);

  const filters: Array<{ id: ActivityFilter; label: string }> = [
    { id: "all", label: t(lang, "activityFilterAll") },
    { id: "created", label: t(lang, "activityFilterCreated") },
    { id: "changed", label: t(lang, "activityFilterChanged") },
    { id: "mine", label: t(lang, "activityFilterMine") },
  ];

  return (
    <aside className="activity">
      <h2>
        <span className="activity-ico" aria-hidden>
          📝
        </span>
        {t(lang, "activity")}
      </h2>

      <div className="activity-toolbar">
        <input
          className="activity-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(lang, "activitySearch")}
          aria-label={t(lang, "activitySearch")}
        />
        <div className="activity-filters" role="tablist" aria-label={t(lang, "activity")}>
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={`activity-filter${filter === f.id ? " is-on" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <MascotEmpty text={t(lang, "emptyActivity")} />
      ) : groups.length === 0 ? (
        <p className="activity-empty-filter">{t(lang, "activityEmptyFilter")}</p>
      ) : (
        groups.map((group) => {
          const open = !collapsed[group.label];
          return (
            <section key={group.label} className="activity-group">
              <button
                type="button"
                className="activity-group-toggle"
                aria-expanded={open}
                onClick={() => setCollapsed((prev) => ({ ...prev, [group.label]: open }))}
              >
                <span className="activity-group-chevron" aria-hidden>
                  {open ? "▼" : "▶"}
                </span>
                <span>{group.label}</span>
                <span className="activity-group-count">({group.rows.length})</span>
              </button>
              {open ? (
                <ul>
                  {group.rows.map((row, i) => {
                    const title = row.title || "Untitled";
                    const kind = activityKind(row);
                    const tip = [verb(row, lang), title, row.editor, row.space_title].filter(Boolean).join(" · ");
                    return (
                      <li key={row.id} style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
                        <Link className="activity-item" to={`/p/${row.id}`} title={tip}>
                          <ActionMark kind={kind} />
                          <div className="activity-body">
                            <div className="activity-top">
                              <time dateTime={row.updated_at}>{clock(row.updated_at)}</time>
                              <span className={`activity-badge is-${kind}`}>{verb(row, lang)}</span>
                            </div>
                            <span className="activity-title">{title}</span>
                            <div className="activity-meta">
                              <EditorAvatar name={row.editor} avatar={row.editor_avatar} />
                              {row.editor ? <span className="activity-who">{row.editor}</span> : null}
                              {row.space_title ? (
                                <span className="activity-space" title={row.space_title}>
                                  {row.space_title}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </section>
          );
        })
      )}
      {rows.length > 4 ? (
        <Link className="activity-all" to="/?tab=recent">
          {t(lang, "activityAll")}
        </Link>
      ) : null}
    </aside>
  );
}

export function HomePage({ lang, me, onChanged }: Props) {
  const [data, setData] = useState<Awaited<ReturnType<typeof hieraApi.home>> | null>(null);
  const loc = useLocation();
  const tab = new URLSearchParams(loc.search).get("tab") || "home";

  useEffect(() => {
    void hieraApi.home().then(setData);
  }, [tab]);

  if (!data) {
    return (
      <div className="home-frame">
        <div className="home-main pad">…</div>
        <ActivitySkeleton />
      </div>
    );
  }

  if (tab === "favorites") {
    return (
      <div className="home pad">
        <nav className="crumbs">
          <Link to="/">{t(lang, "home")}</Link>
          <span>/</span>
          <span>{t(lang, "favorites")}</span>
        </nav>
        <h2>{t(lang, "favorites")}</h2>
        <ul className="recent-list">
          {data.favorites.map((f, i) => (
            <li key={i}>
              {f.page_id ? (
                <Link to={`/p/${f.page_id}`}>{String(f.page_title || "Page")}</Link>
              ) : (
                <Link to={`/s/${f.space_id}`}>{String(f.space_title || "Space")}</Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (tab === "spaces") {
    return (
      <div className="home pad">
        <nav className="crumbs">
          <Link to="/">{t(lang, "home")}</Link>
          <span>/</span>
          <span>{t(lang, "spaces")}</span>
        </nav>
        <WorkspaceBlocks lang={lang} spaces={data.spaces} />
      </div>
    );
  }

  if (tab === "recent") {
    return (
      <div className="home pad">
        <nav className="crumbs">
          <Link to="/">{t(lang, "home")}</Link>
          <span>/</span>
          <span>{t(lang, "recent")}</span>
        </nav>
        <h2>{t(lang, "recent")}</h2>
        <ul className="recent-list">
          {data.recent.map((row) => (
            <li key={row.id}>
              <Link to={`/p/${row.id}`}>
                <strong>{row.title || "Untitled"}</strong>
                <span className="meta">
                  {row.space_title} · {relTime(row.updated_at, lang)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const [lead, ...rest] = data.recent.slice(0, 4);
  const docCount = data.spaces.reduce((n, s) => n + (Number(s.page_count) || 0), 0);
  const weekCount = data.recent.filter((r) => dayDiff(r.updated_at) < 7).length;

  return (
    <div className="home-frame">
      <div className="home-main">
        <nav className="crumbs">
          <span>{t(lang, "home")}</span>
        </nav>
        <AgentChat
          lang={lang}
          name={me.displayName}
          onChanged={() => {
            onChanged();
            void hieraApi.home().then(setData);
          }}
        />

        <WorkspaceBlocks lang={lang} spaces={data.spaces} limit={4} />

        <section className="home-section">
          <h2>{t(lang, "homeStats")}</h2>
          <div className="home-stats">
            <div className="home-stat">
              <strong>{data.spaces.length}</strong>
              <span>{t(lang, "spaces")}</span>
            </div>
            <div className="home-stat">
              <strong>{docCount}</strong>
              <span>{t(lang, "homeDocs")}</span>
            </div>
            <div className="home-stat">
              <strong>{weekCount}</strong>
              <span>{t(lang, "thisWeek")}</span>
            </div>
          </div>
          <div className="home-quick">
            <span className="home-quick-label">{t(lang, "homeQuick")}</span>
            <Link to="/?tab=recent">{t(lang, "recent")}</Link>
            <Link to="/?tab=favorites">{t(lang, "favorites")}</Link>
            <Link to="/templates">{t(lang, "templates")}</Link>
          </div>
        </section>

        <section className="home-section">
          <h2>{t(lang, "continueWork")}</h2>
          {lead ? (
            <>
              <Link className="continue-hero" to={`/p/${lead.id}`}>
                <PageTile icon={lead.icon} />
                <div className="continue-hero-body">
                  <strong title={lead.title || "Untitled"}>{lead.title || "Untitled"}</strong>
                  <span>
                    {lead.space_title} · {t(lang, "lastChange")} · {relTime(lead.updated_at, lang)}
                  </span>
                  <em>{t(lang, "openGo")} →</em>
                </div>
              </Link>
              {rest.length ? (
                <div className="continue-mini">
                  {rest.map((r) => (
                    <Link key={r.id} className="continue-card" to={`/p/${r.id}`}>
                      <PageTile icon={r.icon} />
                      <div className="continue-card-body">
                        <strong title={r.title || "Untitled"}>{r.title || "Untitled"}</strong>
                        <span>
                          {r.space_title} · {relTime(r.updated_at, lang)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <MascotEmpty text={t(lang, "emptyContinue")} />
          )}
        </section>
      </div>
      <Activity lang={lang} me={me} rows={data.recent} />
    </div>
  );
}
